import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectKeyValEntry } from '@shared/ProjectKeyValStore';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { MenuModule } from 'primeng/menu';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Subscription } from 'rxjs';
import { ProjectKeyvalAdminService } from 'src/app/shared/services/project-keyval-admin.service';
import { ProjectService } from 'src/app/shared/services/project.service';
import { SocketAdminService } from '@shared-angular/services/socketadmin.service';
import { ProjectSubmenuComponent } from 'src/app/project-submenu/project-submenu.component';
import { toGroupchatType } from '@shared/ProjectKeyValStore';

export interface BotChatMessage {
  _id: string;
  botName: string;
  message: string;
  timestamp: string;
  key: string;
  type: string;
  userId: string;
  isEdited: boolean;
}

@Component({
  imports: [DatePipe,  FormsModule, ButtonModule, InputTextModule, DialogModule, ConfirmDialogModule, IconFieldModule, InputIconModule, MenuModule, ProjectSubmenuComponent],
  providers: [ConfirmationService],
  templateUrl: './project-groupchat-channel.component.html'
})
export class ProjectGroupchatChannelComponent implements OnInit, OnDestroy {
  @ViewChild('messageMenu') public messageMenu!: Menu;

  public channel: string = '';
  public messages: BotChatMessage[] = [];
  public activeMenuItems: MenuItem[] = [];

  // Edit
  public editingMessage: BotChatMessage | null = null;
  public editMessageText: string = '';
  public showEditDialog: boolean = false;

  private keyvals: ProjectKeyValEntry<string>[] = [];
  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminService: ProjectKeyvalAdminService,
    private projectService: ProjectService,
    private socketService: SocketAdminService,
    private confirmationService: ConfirmationService
  ) { }

  private get projectId(): string {
    return this.projectService.selectedProject?._id ?? '-';
  }

  public async ngOnInit() {
    this.channel = this.route.snapshot.params['channel'] as string;
    await this.loadData();

    this.subs.push(
      this.socketService.onProjectKeyValChange.subscribe((entry) => {
        if (entry.projectId != this.projectService.selectedProject?._id) return;
        const strEntry = { ...entry, value: String(entry.value) } as ProjectKeyValEntry<string>;
        const index = this.keyvals.findIndex(kv => kv._id === entry._id);
        if (index > -1) {
          this.keyvals[index] = strEntry;
        } else {
          this.keyvals.push(strEntry);
        }
        this.buildMessages();
      }),
      this.socketService.onProjectKeyValClean.subscribe(() => {
        void this.loadData();
      }),
      this.socketService.onProjectKeyValDelete.subscribe(() => {
        this.keyvals = [];
        this.buildMessages();
      })
    );
  }

  public ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  public goBack() {
    void this.router.navigate(['/bot-admin/project', this.projectId, 'groupchat']);
  }

  private async loadData() {
    const keyvals = await this.adminService.getProjectKeyVals(this.projectId);
    this.keyvals = keyvals.map(kv => ({ ...kv, value: String(kv.value) }));
    this.buildMessages();
  }

  private buildMessages() {
    const storageType = toGroupchatType(this.channel);
    const channelKeyvals = this.keyvals
      .filter(kv => kv.type === storageType && kv.key.startsWith('msg'))
      .sort((a, b) => a.key.localeCompare(b.key));

    this.messages = channelKeyvals.map(kv => ({
      _id: kv._id,
      botName: this.extractBotName(kv.value),
      message: this.extractMessage(kv.value),
      timestamp: this.extractTimestamp(kv.key),
      key: kv.key,
      type: kv.type,
      userId: kv.userId,
      isEdited: kv.created !== kv.updated
    }));
  }

  // Message actions
  public openMessageMenu(event: Event, msg: BotChatMessage) {
    this.activeMenuItems = [
      {
        label: $localize`:@@edit-message:Nachricht bearbeiten`,
        icon: 'ri-pencil-line',
        command: () => this.startEditMessage(msg)
      },
      {
        label: $localize`:@@delete-message:Nachricht löschen`,
        icon: 'ri-delete-bin-line',
        command: () => this.confirmDeleteMessage(msg)
      }
    ];
    this.messageMenu.toggle(event);
  }

  public startEditMessage(msg: BotChatMessage) {
    this.editingMessage = msg;
    this.editMessageText = msg.message;
    this.showEditDialog = true;
  }

  public cancelEditMessage() {
    this.showEditDialog = false;
    this.editingMessage = null;
    this.editMessageText = '';
  }

  public async saveEditMessage() {
    if (!this.editingMessage) return;
    const newValue = this.editingMessage.botName + ': ' + this.editMessageText.trim();
    await this.adminService.updateProjectKeyVal(this.projectId, this.editingMessage._id, newValue);
    this.editingMessage.message = this.editMessageText.trim();
    this.editingMessage.isEdited = true;
    this.showEditDialog = false;
    this.editingMessage = null;
    this.editMessageText = '';
  }

  private confirmDeleteMessage(msg: BotChatMessage) {
    this.confirmationService.confirm({
      header: $localize`:@@delete-message-confirmation:Nachricht löschen?`,
      message: $localize`:@@delete-message-body:Möchtest du diese Nachricht wirklich löschen? Der KeyVal-Eintrag wird auch gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`,
      icon: 'ri-error-warning-line',
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete-accept:Löschen`,
        severity: 'danger'
      },
      accept: () => {
        void this.deleteMessage(msg);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  private async deleteMessage(msg: BotChatMessage) {
    await this.adminService.deleteProjectKeyVal(msg.userId, this.projectId, msg.key, msg.type);
    this.keyvals = this.keyvals.filter(kv => kv._id !== msg._id);
    this.buildMessages();
  }

  private extractBotName(value: string): string {
    const colonIndex = value.indexOf(': ');
    if (colonIndex > -1) {
      return value.substring(0, colonIndex);
    }
    return 'Unbekannt';
  }

  private extractMessage(value: string): string {
    const colonIndex = value.indexOf(': ');
    if (colonIndex > -1) {
      return value.substring(colonIndex + 2);
    }
    return value;
  }

  private extractTimestamp(key: string): string {
    const prefix = key.startsWith('msg_') ? 'msg_' : 'msg';
    return key.substring(prefix.length);
  }
}
